<template>
  <div class="warranty-claims">
    <div class="warranty-intro mb-4">
        <h3 class="text-light mb-3">Reclamo de Garantía</h3>
        <p class="text-light fs-5">
          Si tus semillas no germinaron correctamente, puedes iniciar un reclamo de garantía aquí. 
          Nuestro equipo revisará tu caso y te contactará a la brevedad.
        </p>
      </div>

      <!-- Progress Bar -->
      <div class="progress-container mb-4">
        <div class="progress">
          <div 
            class="progress-bar" 
            :style="{ width: `${(currentStep / 3) * 100}%` }"
          ></div>
        </div>
        <div class="progress-labels">
          <span 
            v-for="(label, index) in ['Pedido', 'Producto', 'Detalles']" 
            :key="index"
            :class="{ active: currentStep >= index + 1 }"
          >
            {{ label }}
          </span>
        </div>
      </div>

      <form @submit.prevent="submitClaim" class="warranty-form">
        <!-- Step 1: Order Selection -->
        <div v-show="currentStep === 1" class="form-step">
          <h4 class="text-light mb-3">Selecciona tu Pedido</h4>
          <div class="mb-4">
            <select 
              v-model="formData.order_id"
              class="form-select form-select-lg" 
              required
              :disabled="isLoadingOrders"
            >
              <option value="" v-if="isLoadingOrders">Cargando pedidos...</option>
              <option value="" v-else-if="eligibleOrders.length === 0">No tienes pedidos elegibles</option>
              <option value="" v-else>Seleccionar pedido...</option>
              <option 
                v-for="order in eligibleOrders" 
                :key="order.id" 
                :value="order.id"
              >
                Pedido #{{ order.id }} - {{ formatDate(order.created_at) }} - {{ order.recipient_name || 'Cliente' }}
              </option>
            </select>
            <small class="text-muted mt-2 d-block">
              <span v-if="isLoadingOrders">
                <i class="fas fa-spinner fa-spin me-2"></i>
                Cargando tus pedidos...
              </span>
              <span v-else-if="eligibleOrders.length === 0">
                <i class="fas fa-info-circle me-2"></i>
                Solo se muestran pedidos completados de los últimos 30 días
              </span>
              <span v-else>
                Solo se muestran pedidos completados de los últimos 30 días ({{ eligibleOrders.length }} disponibles)
              </span>
            </small>
          </div>
        </div>

        <!-- Step 2: Product Selection -->
        <div v-show="currentStep === 2" class="form-step">
          <h4 class="text-light mb-3">Selecciona el Producto</h4>
          <div class="mb-4">
            <select 
              v-model="formData.product_id"
              class="form-select form-select-lg"
              required
            >
              <option value="">Seleccionar producto...</option>
              <option 
                v-for="product in getOrderProducts(formData.order_id)"
                :key="product.id"
                :value="product.id"
              >
                {{ product.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Step 3: Details -->
        <div v-show="currentStep === 3" class="form-step">
          <h4 class="text-light mb-3">Detalles del Reclamo</h4>
          <div class="mb-4">
            <label class="form-label text-light">Describe el Problema</label>
            <textarea
              v-model="formData.description"
              class="form-control"
              rows="4"
              placeholder="Describe el problema que tuviste con la germinación..."
              required
            ></textarea>
          </div>

          <div class="mb-4">
            <label class="form-label text-light d-flex align-items-center">
              Fotos de Evidencia
              <span class="badge bg-primary ms-2" data-bs-toggle="tooltip" title="Recomendamos adjuntar fotos para agilizar el proceso">
                Recomendado
              </span>
              <span class="badge bg-secondary ms-2" v-if="photos.filter(p => p.file).length > 0">
                {{ photos.filter(p => p.file).length }}/3 fotos
              </span>
            </label>
            <div class="photo-upload-container">
              <label 
                v-for="(photo, index) in photos"
                :key="index"
                class="photo-upload-item"
                :class="{ 'has-photo': photo.preview }"
              >
                <img 
                  v-if="photo.preview" 
                  :src="photo.preview" 
                  class="photo-preview"
                >
                <div v-else class="upload-placeholder">
                  <i class="fas fa-camera"></i>
                  <span class="upload-text">Subir foto</span>
                </div>
                <input 
                  type="file"
                  @change="(e) => handlePhotoSelect(e, index)"
                  accept="image/*"
                  class="d-none"
                >
                <button 
                  v-if="photo.preview"
                  type="button"
                  class="remove-photo"
                  @click.prevent="removePhoto(index)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </label>
            </div>
            <small class="text-muted mt-2 d-block">
              Debes subir al menos una foto para poder enviar el reclamo
            </small>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="d-flex justify-content-between mt-4">
          <button 
            type="button" 
            class="btn btn-outline-primary"
            v-if="currentStep > 1"
            @click="currentStep--"
          >
            <i class="fas fa-arrow-left me-2"></i>
            Anterior
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            v-if="currentStep < 3"
            @click="nextStep"
            :disabled="!canProceed"
          >
            Siguiente
            <i class="fas fa-arrow-right ms-2"></i>
          </button>
          <button 
            type="submit" 
            class="btn btn-primary"
            v-if="currentStep === 3"
            :disabled="isSubmitting || !canSubmit"
          >
            <span v-if="isSubmitting">
              <i class="fas fa-spinner fa-spin me-2"></i>
              Enviando...
            </span>
            <span v-else>
              <i class="fas fa-paper-plane me-2"></i>
              Enviar Reclamo
            </span>
          </button>
        </div>
        
        <!-- Help message when form is incomplete -->
        <div v-if="currentStep === 3 && !isFormValid" class="alert alert-info mt-3">
          <i class="fas fa-info-circle me-2"></i>
          <strong>Para enviar el reclamo necesitas:</strong>
          <ul class="mb-0 mt-2">
            <li v-if="!formData.description || formData.description.trim().length === 0">
              Describir el problema con la germinación
            </li>
            <li v-if="!photos.some(photo => photo.file)">
              Subir al menos una foto como evidencia
            </li>
          </ul>
        </div>
        
        <!-- Debug info -->
        <div v-if="currentStep === 3" class="alert alert-warning mt-3">
          <strong>Debug Info:</strong><br>
          Botón habilitado: {{ !isSubmitting && canSubmit }}<br>
          canSubmit: {{ canSubmit }}<br>
          isFormValid: {{ isFormValid }}<br>
          hasDescription: {{ formData.description && formData.description.trim().length > 0 }}<br>
          hasPhotos: {{ photos.some(photo => photo.file) }}<br>
          description: "{{ formData.description }}"<br>
          photos with files: {{ photos.filter(p => p.file).length }}
        </div>
      </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { createWarrantyClaim } from '@/services/warranty'
import { getUserOrders } from '@/services/orders'
import { useStore } from 'vuex'

const store = useStore()
const toast = useToast()

const formData = ref({
  order_id: '',
  product_id: '',
  description: ''
})

const photos = ref([
  { file: null, preview: null },
  { file: null, preview: null },
  { file: null, preview: null }
])

const isSubmitting = ref(false)
const currentStep = ref(1)
const isLoadingOrders = ref(true)
const userOrders = ref([])

const canProceed = computed(() => {
  if (currentStep.value === 1) return formData.value.order_id
  if (currentStep.value === 2) return formData.value.product_id
  return true
})

const canSubmit = computed(() => {
  const hasDescription = formData.value.description && formData.value.description.trim().length > 0
  const hasPhotos = photos.value.some(photo => photo.file)
  
  console.log('canSubmit debug:', {
    hasDescription,
    hasPhotos,
    description: formData.value.description,
    photos: photos.value.map(p => ({ hasFile: !!p.file, hasPreview: !!p.preview }))
  })
  
  return hasDescription && hasPhotos
})

// Alternative computed that forces reactivity
const isFormValid = computed(() => {
  const desc = formData.value.description?.trim() || ''
  const photoCount = photos.value.filter(p => p.file).length
  console.log('isFormValid check:', { desc, photoCount, result: desc.length > 0 && photoCount > 0 })
  return desc.length > 0 && photoCount > 0
})

const nextStep = () => {
  if (currentStep.value < 3 && canProceed.value) {
    currentStep.value++
  }
}


// Obtener órdenes elegibles para garantía (últimos 30 días y completadas)
const eligibleOrders = computed(() => {
  if (!userOrders.value) return []
  
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  
  return userOrders.value.filter(order => 
    order.status === 'completed' && 
    new Date(order.created_at) > thirtyDaysAgo
  )
})

// Cargar órdenes del usuario al montar el componente
onMounted(async () => {
  try {
    isLoadingOrders.value = true
    const response = await getUserOrders()
    if (response && response.orders) {
      userOrders.value = response.orders
    }
  } catch (error) {
    console.error('Error loading user orders:', error)
    toast.error('Error al cargar tus pedidos')
  } finally {
    isLoadingOrders.value = false
  }
})

// Methods
const getOrderProducts = (orderId) => {
  const order = userOrders.value?.find(o => o.id == orderId)
  return order?.products?.map(p => p.product_details) || []
}

const handlePhotoSelect = (event, index) => {
  const file = event.target.files[0]
  if (!file) return

  // Validate file type and size
  if (!file.type.startsWith('image/')) {
    toast.error('Por favor selecciona una imagen válida')
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    toast.error('La imagen no debe superar los 5MB')
    return
  }

  // Create preview
  const reader = new FileReader()
  reader.onload = (e) => {
    // Ensure reactivity by using array methods that trigger updates
    const newPhotos = [...photos.value]
    newPhotos[index] = {
      file,
      preview: e.target.result
    }
    photos.value = newPhotos
    
    console.log('Photo added at index', index, 'total photos with files:', photos.value.filter(p => p.file).length)
  }
  reader.readAsDataURL(file)
}

const removePhoto = (index) => {
  // Ensure reactivity by creating a new array
  const newPhotos = [...photos.value]
  newPhotos[index] = { file: null, preview: null }
  photos.value = newPhotos
  
  console.log('Photo removed at index', index, 'remaining photos with files:', photos.value.filter(p => p.file).length)
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('es-ES')
}

const submitClaim = async () => {
  try {
    // Log form state before submitting
    console.log('Form state before submit:', { 
      formData: { ...formData.value }, 
      photos: photos.value.map(p => ({ hasFile: !!p.file })), 
      isFormValid: isFormValid.value,
      canSubmit: canSubmit.value
    })
    
    isSubmitting.value = true

    // Validate required fields
    if (!formData.value.order_id || !formData.value.product_id || !formData.value.description) {
      toast.error('Por favor completa todos los campos requeridos')
      isSubmitting.value = false
      return
    }

    // Validate at least one photo is uploaded
    const hasPhotos = photos.value.some(photo => photo.file)
    if (!hasPhotos) {
      toast.error('Debes subir al menos una foto')
      isSubmitting.value = false
      return
    }

    // Prepare form data
    const formDataToSend = new FormData()
    formDataToSend.append('order_id', formData.value.order_id)
    formDataToSend.append('product_id', formData.value.product_id)
    formDataToSend.append('description', formData.value.description)

    // Get order and product details
    const selectedOrder = userOrders.value.find(o => o.id == formData.value.order_id)
    const selectedProduct = getOrderProducts(formData.value.order_id).find(p => p.id == formData.value.product_id)

    if (selectedOrder) {
      formDataToSend.append('name', selectedOrder.recipient_name || 'Cliente')
      formDataToSend.append('email', selectedOrder.customer_email || store.state.user?.email || '')
      formDataToSend.append('purchase_date', selectedOrder.created_at.split('T')[0])
      formDataToSend.append('germination_date', new Date().toISOString().split('T')[0])
    }

    if (selectedProduct) {
      formDataToSend.append('product_name', selectedProduct.name)
    }

    // Append photos
    photos.value.forEach((photo) => {
      if (photo.file) {
        formDataToSend.append('photos[]', photo.file)
      }
    })

    await createWarrantyClaim(formDataToSend)

    // Reset form
    formData.value = {
      order_id: '',
      product_id: '',
      description: ''
    }
    photos.value = photos.value.map(() => ({ file: null, preview: null }))
    currentStep.value = 1

    toast.success('Reclamo enviado exitosamente. Te contactaremos en 48-72 horas hábiles.')
  } catch (error) {
    console.error('Error submitting claim:', error)
    toast.error(error.message || 'Error al enviar el reclamo')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.warranty-claims {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.warranty-form {
  background: var(--black-color-2);
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.warranty-description {
  max-width: 600px;
  margin: 0 auto;
}

.progress-container {
  padding: 0 1rem;
}

.progress {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  margin-bottom: 1rem;
}

.progress-bar {
  height: 100%;
  background: var(--primary-color);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.progress-labels span {
  position: relative;
  padding-top: 0.5rem;
}

.progress-labels span::before {
  content: '';
  position: absolute;
  top: -0.75rem;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  transition: background-color 0.3s ease;
}

.progress-labels span.active {
  color: var(--primary-color);
}

.progress-labels span.active::before {
  background: var(--primary-color);
}

.steps-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0;
  position: relative;
  padding: 0 2rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  flex: 1;
}

.step::before {
  content: '';
  position: absolute;
  top: 1.5rem;
  left: -50%;
  width: 100%;
  height: 2px;
  background-color: var(--primary-color);
  opacity: 0.3;
}

.step:first-child::before {
  display: none;
}

.step.active .step-number {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.step.completed .step-number {
  background-color: var(--success-color);
  border-color: var(--success-color);
}

.step-number {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.step-text {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.photo-upload-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
}

.photo-upload-item {
  aspect-ratio: 1;
  border: 2px dashed var(--primary-color);
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--primary-color);
}

.upload-placeholder i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-size: 0.9rem;
}

.photo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-photo {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.remove-photo:hover {
  background: rgba(0, 0, 0, 0.7);
}
</style>
